import React, { FC, useRef, useEffect, useState } from 'react';
import mapboxgl, { LngLatLike } from 'mapbox-gl';
import './map.component.css';
import { calculateCenterCoordinate, calculateZoomLevel } from './map-helpers.component';

interface COETrainingRouteMapProps {
  coordinates: [number, number][];
  width?: string | number;
  height?: string | number;
}

const COETrainingRouteMapComponent: FC<COETrainingRouteMapProps> = ({ coordinates, width, height }) => {
  const mapDiv = useRef<HTMLDivElement>(null);
  const [map, setMap] = useState<mapboxgl.Map | null>(null);

  // Function to check if coordinates are in E7 format and convert if needed
  const normalizeCoordinates = (coords: [number, number][]) => {
    return coords.map(([lng, lat]) => {
      // If values are outside the valid range, assume they are in E7 format and convert them
      if (Math.abs(lat) > 90 || Math.abs(lng) > 180) {
        return [lng / 1e7, lat / 1e7]; // Convert from E7 format
      }
      return [lng, lat]; // Already in correct format
    });
  };

  useEffect(() => {
    if (!coordinates.length || coordinates.length < 2) return;

    const attachMap = () => {
      mapboxgl.accessToken = process.env.REACT_APP_MAPBOX_ACCESS_TOKEN as string;
      const formattedCoordinates = normalizeCoordinates(coordinates) as any;

      const zoomLevel = calculateZoomLevel(formattedCoordinates, 600, 300);
      const mapCenter = calculateCenterCoordinate(formattedCoordinates);
      const bounds = new mapboxgl.LngLatBounds();
      formattedCoordinates.forEach((coord) => bounds.extend(coord));

      const padding = 0.1;
      bounds.extend([bounds.getSouthWest().lng - padding * 2, bounds.getSouthWest().lat - padding * 1]);
      bounds.extend([bounds.getNorthEast().lng + padding * 2, bounds.getNorthEast().lat + padding * 1]);

      const map = new mapboxgl.Map({
        container: mapDiv.current || '',
        style: 'mapbox://styles/mapbox/outdoors-v11',
        center: mapCenter as LngLatLike,
        zoom: zoomLevel - 2,
        attributionControl: false,
      });

      map.setMaxBounds(bounds);
      map.setMinZoom(zoomLevel - 2);
      map.setMaxZoom(zoomLevel + 2);

      map.on('load', () => {
        map.addSource('route', {
          type: 'geojson',
          data: {
            type: 'Feature',
            properties: { animate: false },
            geometry: {
              type: 'LineString',
              coordinates: formattedCoordinates,
            },
          },
        });

        map.addLayer({
          id: 'route',
          type: 'line',
          source: 'route',
          layout: {
            'line-join': 'round',
            'line-cap': 'round',
          },
          paint: {
            'line-color': 'red',
            'line-width': 3,
          },
        });

        setMap(map);
      });
    };

    !map && attachMap();
  }, [coordinates]);

  return (
    <div className="map-container" style={{ width: width ?? '600px', height: height ?? '300px' }}>
      <div
        ref={mapDiv}
        className="map"
        style={{ width: width ?? '600px', height: height ?? '300px', maxHeight: height ?? '300px' }}
      />
    </div>
  );
};

export default COETrainingRouteMapComponent;
